import React, { useEffect, useMemo, useState } from "react";
import {
  FaChevronDown,
  FaCloudUploadAlt,
  FaFileAlt,
  FaLock,
  FaMoneyCheckAlt,
  FaRegSave,
  FaTrashAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { useAppTheme } from "../context/AppThemeContext";
import { useStudentProfile } from "../context/StudentProfileContext";
import { ADMIN_ROLES, STUDENT_ROLES, normalizeRole } from "../utils/authRole";
import {
  DOCUMENT_RULES,
  TOTAL_UPLOAD_QUOTA_BYTES,
  formatBytes,
  formatCurrency,
} from "../utils/admissionWorkflow";

const ADMISSION_FORM_DRAFT_KEY = "admissionFormDraft";
const ADMISSION_DOCUMENTS_DRAFT_KEY = "admissionDocumentsDraft";
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY || "";

const ADMISSION_FEE_BY_STANDARD = {
  "1st": 1000,
  "2nd": 1200,
  "3rd": 1400,
  "4th": 1600,
  "5th": 1800,
  "6th": 2000,
  "7th": 2200,
  "8th": 2400,
  "9th": 2600,
  "10th": 3000,
  "11th": 3400,
  "12th": 3800,
};

const getAdmissionFeeForStandard = (standard) => ADMISSION_FEE_BY_STANDARD[standard] || 0;

const toNameParts = (rawValue) => {
  const cleaned = String(rawValue || "")
    .split("@")[0]
    .replace(/[._-]+/g, " ")
    .trim();

  if (!cleaned) {
    return { firstName: "", lastName: "" };
  }

  const words = cleaned
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

  return {
    firstName: words[0] || "",
    lastName: words.slice(1).join(" "),
  };
};

const getNamePartsFromStudent = (studentInfo = {}, fallbackName = "") => {
  if (studentInfo.firstName || studentInfo.lastName) {
    return {
      firstName: String(studentInfo.firstName || "").trim(),
      lastName: String(studentInfo.lastName || "").trim(),
    };
  }

  return toNameParts(studentInfo.name || studentInfo.fullName || fallbackName);
};

const getStudentPrefill = (studentProfile, fallbackName, fallbackEmail) => {
  const studentInfo = studentProfile?.studentInfo || {};
  const nameParts = getNamePartsFromStudent(studentInfo, fallbackName);

  return {
    firstName: nameParts.firstName,
    lastName: nameParts.lastName,
    email:
      studentInfo.email
      || studentProfile?.email
      || fallbackEmail,
    contactNumber:
      studentInfo.contactNumber
      || studentInfo.mobile
      || studentInfo.phone
      || studentProfile?.contactNumber
      || "",
    emergencyContact:
      studentInfo.emergencyContact
      || studentInfo.parentContact
      || studentInfo.guardianContact
      || "",
    address: studentInfo.address || studentInfo.currentAddress || "",
    city: studentInfo.city || "",
    state: studentInfo.state || "",
    pincode: studentInfo.pincode || studentInfo.pinCode || "",
    adharNumber:
      studentInfo.adharNumber
      || studentInfo.aadharNumber
      || studentInfo.aadhaarNumber
      || "",
    gender: studentInfo.gender || "",
    bloodGroup: studentInfo.bloodGroup || "",
    standard:
      studentInfo.studentClass
      || studentInfo.standard
      || studentInfo.className
      || "",
    previousSchool: studentInfo.previousSchool || "",
    fatherName: studentInfo.fatherName || "",
    motherName: studentInfo.motherName || "",
    dateOfBirth: studentInfo.dateOfBirth || studentInfo.dob || "",
  };
};

const getInitialFormData = (
  today,
  { isStudentRole = false, isAdminRole = false, studentPrefill = null } = {}
) => {
  const draft = safeSessionGet(ADMISSION_FORM_DRAFT_KEY, null);
  const next = {
    ...createInitialDraft(today),
    ...(draft || {}),
    admissionDate: today,
  };

  if (isStudentRole && studentPrefill) {
    next.firstName = studentPrefill.firstName || "";
    next.lastName = studentPrefill.lastName || "";
    next.email = studentPrefill.email || "";
    next.contactNumber = studentPrefill.contactNumber || "";
    next.emergencyContact = studentPrefill.emergencyContact || "";
    next.address = studentPrefill.address || "";
    next.city = studentPrefill.city || "";
    next.state = studentPrefill.state || "";
    next.pincode = studentPrefill.pincode || "";
    next.adharNumber = studentPrefill.adharNumber || "";
    next.gender = studentPrefill.gender || "";
    next.bloodGroup = studentPrefill.bloodGroup || "";
    next.standard = studentPrefill.standard || "";
    next.previousSchool = studentPrefill.previousSchool || "";
    next.fatherName = studentPrefill.fatherName || "";
    next.motherName = studentPrefill.motherName || "";
    next.dateOfBirth = studentPrefill.dateOfBirth || "";
    next.admissionAmount = getAdmissionFeeForStandard(next.standard);
  }

  if (isAdminRole) {
    next.firstName = "";
    next.lastName = "";
  }

  return next;
};

const INITIAL_UPLOADS = {
  studentPhoto: null,
  aadharCard: null,
  birthCertificate: null,
  transferCertificate: null,
  previousMarksheet: null,
  addressProof: null,
};

const createInitialDraft = (today) => ({
  firstName: "",
  lastName: "",
  fatherName: "",
  motherName: "",
  guardianOccupation: "",
  dateOfBirth: "",
  gender: "",
  bloodGroup: "",
  standard: "",
  previousSchool: "",
  contactNumber: "",
  emergencyContact: "",
  email: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  adharNumber: "",
  admissionAmount: 0,
  admissionDate: today,
});

const INITIAL_CHECKLIST = {
  studentPhoto: false,
  aadharCard: false,
  birthCertificate: false,
  transferCertificate: false,
  previousMarksheet: false,
  addressProof: false,
};

const safeSessionGet = (key, fallbackValue) => {
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallbackValue;
  } catch {
    return fallbackValue;
  }
};

const isPdfFile = (file) => file && (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf"));
const isImageFile = (file) => file && file.type.startsWith("image/");

const FormThemeCtx = React.createContext(true);

export default function AdmissionForm({ embedded = false }) {
  const today = new Date().toISOString().split("T")[0];
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();
  const { studentProfile } = useStudentProfile();
  const normalizedRole = normalizeRole(localStorage.getItem("user"));
  const isStudentRole = STUDENT_ROLES.includes(normalizedRole);
  const isAdminRole = ADMIN_ROLES.includes(normalizedRole);
  const currentUserName =
    localStorage.getItem("currentUserName")
    || localStorage.getItem("username")
    || localStorage.getItem("authEmail")
    || "";
  const authEmail = localStorage.getItem("authEmail") || "";
  const studentPrefill = useMemo(
    () => getStudentPrefill(studentProfile, currentUserName, authEmail),
    [studentProfile, currentUserName, authEmail]
  );

  const [formData, setFormData] = useState(() =>
    getInitialFormData(today, { isStudentRole, isAdminRole, studentPrefill })
  );
  const [documentChecklist, setDocumentChecklist] = useState(() => safeSessionGet(ADMISSION_DOCUMENTS_DRAFT_KEY, INITIAL_CHECKLIST));
  const [uploads, setUploads] = useState(INITIAL_UPLOADS);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAdmissionId, setSavedAdmissionId] = useState(null);
  const [paymentRetry, setPaymentRetry] = useState(null);
  const [prefillShown, setPrefillShown] = useState(false);
  const [lastStandard, setLastStandard] = useState(formData.standard);

  // 🔒 Only lock fields that are actually prefilled with data
  const studentLockedFields = useMemo(
    () => {
      if (!isStudentRole || !studentPrefill) return new Set();
      const locked = new Set();
      if (studentPrefill.firstName) locked.add("firstName");
      if (studentPrefill.lastName) locked.add("lastName");
      if (studentPrefill.email) locked.add("email");
      if (studentPrefill.contactNumber) locked.add("contactNumber");
      if (studentPrefill.emergencyContact) locked.add("emergencyContact");
      if (studentPrefill.address) locked.add("address");
      if (studentPrefill.city) locked.add("city");
      if (studentPrefill.state) locked.add("state");
      if (studentPrefill.pincode) locked.add("pincode");
      if (studentPrefill.adharNumber) locked.add("adharNumber");
      if (studentPrefill.gender) locked.add("gender");
      if (studentPrefill.bloodGroup) locked.add("bloodGroup");
      if (studentPrefill.standard) locked.add("standard");
      if (studentPrefill.previousSchool) locked.add("previousSchool");
      if (studentPrefill.fatherName) locked.add("fatherName");
      if (studentPrefill.motherName) locked.add("motherName");
      return locked;
    },
    [isStudentRole, studentPrefill]
  );

  const isFieldReadOnlyForStudent = (name) => isStudentRole && studentLockedFields.has(name);

  const standardOptions = ["1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th", "11th", "12th"];
  const genderOptions = ["Female", "Male", "Other"];
  const bloodGroupOptions = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // 🔄 ENHANCEMENT 1: Auto-save draft every 30 seconds for students
  useEffect(() => {
    if (!isStudentRole) return;

    const autoSaveInterval = setInterval(() => {
      sessionStorage.setItem(ADMISSION_FORM_DRAFT_KEY, JSON.stringify(formData));
      sessionStorage.setItem(ADMISSION_DOCUMENTS_DRAFT_KEY, JSON.stringify(documentChecklist));
    }, 30000);

    return () => clearInterval(autoSaveInterval);
  }, [formData, documentChecklist, isStudentRole]);

  useEffect(() => {
    sessionStorage.setItem(ADMISSION_FORM_DRAFT_KEY, JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    sessionStorage.setItem(ADMISSION_DOCUMENTS_DRAFT_KEY, JSON.stringify(documentChecklist));
  }, [documentChecklist]);

  // 🔄 ENHANCEMENT 2: Show fee update toast when standard changes
  useEffect(() => {
    if (formData.standard && lastStandard && formData.standard !== lastStandard) {
      const newFee = getAdmissionFeeForStandard(formData.standard);
      toast.info(`Admission fee for ${formData.standard} Standard: ${formatCurrency(newFee)}`, {
        position: "top-center",
        autoClose: 2500,
      });
    }
    setLastStandard(formData.standard);
  }, [formData.standard]);

  useEffect(() => {
    if (formData.admissionDate !== today) {
      setFormData((current) => ({ ...current, admissionDate: today }));
    }
  }, [formData.admissionDate, today]);

  useEffect(() => {
    if (!isStudentRole || !studentPrefill) return;

    setFormData((current) => {
      const updated = { ...current };
      if (studentPrefill.firstName) updated.firstName = studentPrefill.firstName;
      if (studentPrefill.lastName) updated.lastName = studentPrefill.lastName;
      if (studentPrefill.email) updated.email = studentPrefill.email;
      if (studentPrefill.contactNumber) updated.contactNumber = studentPrefill.contactNumber;
      if (studentPrefill.emergencyContact) updated.emergencyContact = studentPrefill.emergencyContact;
      if (studentPrefill.address) updated.address = studentPrefill.address;
      if (studentPrefill.city) updated.city = studentPrefill.city;
      if (studentPrefill.state) updated.state = studentPrefill.state;
      if (studentPrefill.pincode) updated.pincode = studentPrefill.pincode;
      if (studentPrefill.adharNumber) updated.adharNumber = studentPrefill.adharNumber;
      if (studentPrefill.gender) updated.gender = studentPrefill.gender;
      if (studentPrefill.bloodGroup) updated.bloodGroup = studentPrefill.bloodGroup;
      if (studentPrefill.standard) updated.standard = studentPrefill.standard;
      if (studentPrefill.previousSchool) updated.previousSchool = studentPrefill.previousSchool;
      if (studentPrefill.fatherName) updated.fatherName = studentPrefill.fatherName;
      if (studentPrefill.motherName) updated.motherName = studentPrefill.motherName;
      if (studentPrefill.dateOfBirth) updated.dateOfBirth = studentPrefill.dateOfBirth;
      if (studentPrefill.standard) {
        updated.admissionAmount = getAdmissionFeeForStandard(studentPrefill.standard);
      }
      return updated;
    });

    // 📢 Show prefill confirmation alert
    if (!prefillShown) {
      const filledFields = [
        { name: "firstName", value: studentPrefill.firstName },
        { name: "lastName", value: studentPrefill.lastName },
        { name: "email", value: studentPrefill.email },
        { name: "contactNumber", value: studentPrefill.contactNumber },
        { name: "address", value: studentPrefill.address },
        { name: "dateOfBirth", value: studentPrefill.dateOfBirth },
        { name: "adharNumber", value: studentPrefill.adharNumber },
      ].filter((f) => f.value);
      
      if (filledFields.length > 0) {
        toast.info(
          `${filledFields.length} fields auto-filled from your profile — these are locked for data integrity`,
          {
            position: "top-right",
            autoClose: 4000,
          }
        );
        setPrefillShown(true);
      }
    }
  }, [
    isStudentRole,
    studentPrefill,
    prefillShown,
  ]);

  useEffect(() => {
    if (!formData.standard) {
      if (Number(formData.admissionAmount || 0) !== 0) {
        setFormData((current) => ({ ...current, admissionAmount: 0 }));
      }
      return;
    }

    const nextAmount = getAdmissionFeeForStandard(formData.standard);
    if (Number(formData.admissionAmount || 0) !== nextAmount) {
      setFormData((current) => ({ ...current, admissionAmount: nextAmount }));
    }
  }, [formData.standard, formData.admissionAmount]);

  const uploadedBytes = useMemo(
    () => Object.values(uploads).reduce((total, file) => total + (file?.size || 0), 0),
    [uploads]
  );

  const requiredDocumentCount = useMemo(
    () => Object.values(DOCUMENT_RULES).filter((rule) => rule.required).length,
    []
  );

  const uploadedRequiredCount = useMemo(
    () => Object.entries(DOCUMENT_RULES).filter(([key, rule]) => rule.required && Boolean(uploads[key])).length,
    [uploads]
  );

  const completedFieldCount = useMemo(() => {
    const requiredFields = [
      "firstName",
      "lastName",
      "dateOfBirth",
      "gender",
      "standard",
      "contactNumber",
      "email",
      "adharNumber",
      "address",
      "city",
      "state",
    ];

    return requiredFields.filter((key) => String(formData[key] || "").trim()).length;
  }, [formData]);

  const readinessPercent = Math.min(
    100,
    Math.round(((completedFieldCount / 11) * 0.72 + (uploadedRequiredCount / requiredDocumentCount) * 0.28) * 100)
  );

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.firstName.trim()) nextErrors.firstName = "First name is required.";
    if (!formData.lastName.trim()) nextErrors.lastName = "Last name is required.";
    if (!formData.standard) nextErrors.standard = "Class or standard is required.";
    if (!formData.dateOfBirth) nextErrors.dateOfBirth = "Date of birth is required.";
    else {
      const dob = new Date(formData.dateOfBirth);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dob >= today) nextErrors.dateOfBirth = "Date of birth cannot be today or a future date.";
    }
    if (!formData.gender) nextErrors.gender = "Gender is required.";
    if (!formData.contactNumber) nextErrors.contactNumber = "Contact number is required.";
    else if (!/^\d{10}$/.test(formData.contactNumber)) nextErrors.contactNumber = "Contact number must be 10 digits.";
    if (!formData.email) nextErrors.email = "Email is required.";
    else if (!/^\S+@\S+\.\S+$/.test(formData.email)) nextErrors.email = "Email format is invalid.";
    if (!formData.adharNumber) nextErrors.adharNumber = "Aadhaar number is required.";
    else if (!/^\d{12}$/.test(formData.adharNumber)) nextErrors.adharNumber = "Aadhaar number must be 12 digits.";
    if (formData.emergencyContact && !/^\d{10}$/.test(formData.emergencyContact)) nextErrors.emergencyContact = "Emergency contact must be 10 digits.";
    if (formData.pincode && !/^\d{6}$/.test(formData.pincode)) nextErrors.pincode = "Pincode must be 6 digits.";
    if (!uploads.studentPhoto) nextErrors.studentPhoto = "Student photo is required.";
    if (!uploads.aadharCard) nextErrors.aadharCard = "Aadhaar card copy is required.";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (isFieldReadOnlyForStudent(name)) {
      return;
    }

    setFormData((current) => {
      if (name === "admissionDate") {
        return { ...current, admissionDate: today };
      }

      if (name === "standard") {
        return {
          ...current,
          standard: value,
          admissionAmount: getAdmissionFeeForStandard(value),
        };
      }

      return { ...current, [name]: value };
    });
  };

  const handleChecklistToggle = (key) => {
    setDocumentChecklist((current) => ({ ...current, [key]: !current[key] }));
  };

  const handleFileChange = (event) => {
    const { name, files } = event.target;
    const file = files && files[0] ? files[0] : null;
    const rule = DOCUMENT_RULES[name];

    if (!file) {
      setUploads((current) => ({ ...current, [name]: null }));
      setDocumentChecklist((current) => ({ ...current, [name]: false }));
      return;
    }

    const projectedTotal = uploadedBytes - (uploads[name]?.size || 0) + file.size;

    if (rule?.maxBytes && file.size > rule.maxBytes) {
      toast.error(`${rule.label} must be ${formatBytes(rule.maxBytes)} or smaller.`, { position: "top-center" });
      event.target.value = "";
      return;
    }

    if (projectedTotal > TOTAL_UPLOAD_QUOTA_BYTES) {
      toast.error(`Total upload quota is ${formatBytes(TOTAL_UPLOAD_QUOTA_BYTES)} per application.`, { position: "top-center" });
      event.target.value = "";
      return;
    }

    if (name === "studentPhoto" && !isImageFile(file)) {
      toast.error("Student photo must be an image file.", { position: "top-center" });
      event.target.value = "";
      return;
    }

    if (name !== "studentPhoto" && !isImageFile(file) && !isPdfFile(file)) {
      toast.error("Documents must be PDF or image files.", { position: "top-center" });
      event.target.value = "";
      return;
    }

    setUploads((current) => ({ ...current, [name]: file }));
    setDocumentChecklist((current) => ({ ...current, [name]: true }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const handleSaveDraft = () => {
    sessionStorage.setItem(ADMISSION_FORM_DRAFT_KEY, JSON.stringify(formData));
    sessionStorage.setItem(ADMISSION_DOCUMENTS_DRAFT_KEY, JSON.stringify(documentChecklist));
    toast.success("Draft saved on this device.", { position: "top-center" });
  };

  const handleReset = () => {
    if (!window.confirm("Reset the entire form? All entered data and uploaded files will be cleared.")) return;
    setFormData(getInitialFormData(today, { isStudentRole, isAdminRole, studentPrefill }));
    setUploads(INITIAL_UPLOADS);
    setDocumentChecklist(INITIAL_CHECKLIST);
    setErrors({});
    sessionStorage.removeItem(ADMISSION_FORM_DRAFT_KEY);
    sessionStorage.removeItem(ADMISSION_DOCUMENTS_DRAFT_KEY);
    toast.info("Form has been reset.", { position: "top-center" });
  };

  const openPreview = (file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the validation errors before continuing.", { position: "top-center" });
      return;
    }

    setSaving(true);
    try {
      const admissionPayload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        // The backend must calculate admission fees from the selected
        // standard/application. Client-side amounts are display-only.
        if (key === "admissionDate" || key === "admissionAmount") return;
        if (value !== undefined && value !== null) {
          admissionPayload.append(key, value);
        }
      });
      admissionPayload.append("admissionDate", today);

      Object.entries(uploads).forEach(([key, file]) => {
        if (file) admissionPayload.append(key, file);
      });

      const admissionResponse = await api.post(API_PATHS.admission.save, admissionPayload);
      const admissionId = admissionResponse?.data?.admissionId || admissionResponse?.data?.id;

      if (!admissionId) {
        throw new Error("Application was saved but the server did not return an admission ID.");
      }

      setSavedAdmissionId(admissionId);
      toast.success("Application saved successfully! Review and proceed to payment.", {
        position: "top-center",
        autoClose: 3500,
      });
    } catch (error) {
      toast.error(error?.message || "Failed to save application. Please try again.", { position: "top-center" });
    } finally {
      setSaving(false);
    }
  };

  const handleProceedToPayment = async () => {
    if (!savedAdmissionId) return;

    if (!RAZORPAY_KEY) {
      toast.error("Payment is temporarily unavailable. Missing payment configuration.", { position: "top-center" });
      return;
    }

    setSubmitting(true);
    try {
      const orderRes = await api.post(
        API_PATHS.admission.pay,
        { admissionId: savedAdmissionId }
      );

      const { id, amount, currency, receipt } = orderRes.data || {};

      if (typeof window.Razorpay !== "function") {
        throw new Error("Payment gateway failed to load. Please try again.");
      }

      const options = {
        key: RAZORPAY_KEY,
        amount,
        currency,
        receipt,
        name: "Campus18 Admissions",
        description: "Admission Fee Payment",
        order_id: id,
        handler: async (response) => {
          try {
            await api.post(
              API_PATHS.admission.verify,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                admissionId: savedAdmissionId,
              }
            );

            toast.success("Payment successful and admission submitted!", {
              position: "top-center",
              autoClose: 3000,
            });

            sessionStorage.removeItem(ADMISSION_FORM_DRAFT_KEY);
            sessionStorage.removeItem(ADMISSION_DOCUMENTS_DRAFT_KEY);

            setTimeout(() => navigate("/view-admission-form"), 1200);
          } catch (verifyError) {
            toast.error(verifyError?.message || "Payment verification failed. Please contact support.", {
              position: "top-center",
            });
          } finally {
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => setSubmitting(false),
        },
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
          contact: formData.contactNumber,
        },
        theme: { color: "#1e3a8a" },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      setPaymentRetry({
        error: error?.message || "Payment failed. Please try again.",
        admissionData: formData,
      });
      toast.error(error?.message || "Payment failed. Please try again.", { position: "top-center" });
      setSubmitting(false);
    }
  };

  const inputCls = (name) =>
    `w-full rounded-xl border px-4 py-2.5 text-sm transition focus:outline-none focus:ring-2 ${
      isDarkMode
        ? "bg-slate-800/70 text-white placeholder:text-slate-500"
        : "bg-white text-slate-900 placeholder:text-slate-400"
    } ${
      errors[name]
        ? "border-rose-500/60 focus:ring-rose-500/20"
        : isDarkMode
          ? "border-slate-700 focus:border-blue-500 focus:ring-blue-500/20"
          : "border-slate-300 focus:border-blue-500 focus:ring-blue-500/20"
    }`;

  const err = (name) =>
    errors[name] ? <p className="mt-1 text-xs text-rose-400">{errors[name]}</p> : null;

  const uploadFields = Object.keys(INITIAL_UPLOADS).reduce((acc, name) => {
    acc[name] = {
      name,
      label: DOCUMENT_RULES[name].label,
      accept: name === "studentPhoto" ? "image/*" : ".pdf,image/*",
      required: DOCUMENT_RULES[name].required,
      file: uploads[name],
    };
    return acc;
  }, {});

  return (
    <FormThemeCtx.Provider value={isDarkMode}>
    <div className={embedded ? "px-0 py-0" : `min-h-screen px-4 py-8 sm:px-6 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
      <div className={embedded ? "max-w-none" : "mx-auto max-w-4xl"}>

        {/* Page header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-teal-500 shadow-lg shadow-blue-900/40">
              <FaFileAlt className="text-lg text-white" />
            </div>
            <div>
              <h1 className={`text-xl font-bold sm:text-2xl ${isDarkMode ? "text-white" : "text-slate-900"}`}>Student Admission Form</h1>
              <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Fill all sections, upload documents, proceed to payment</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
              <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${isDarkMode ? "bg-teal-950/50 text-teal-300" : "bg-teal-100 text-teal-700"}`}>
                {uploadedRequiredCount}/{requiredDocumentCount} docs
              </span>
              &ensp;·&ensp;
              <span className="font-semibold text-teal-500">{formatCurrency(formData.admissionAmount)}</span>
            </span>
            <div className="flex w-32 items-center gap-2">
              <div className={`flex-1 overflow-hidden rounded-full ${isDarkMode ? "bg-slate-800" : "bg-slate-200"}`}>
                <div
                  className="h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 transition-all duration-500"
                  style={{ width: `${readinessPercent}%` }}
                />
              </div>
              <span className={`w-8 text-right text-xs font-bold ${isDarkMode ? "text-white" : "text-slate-800"}`}>{readinessPercent}%</span>
            </div>
          </div>
        </div>

        {/* 🔄 ENHANCEMENT 5: Payment retry alert */}
        {paymentRetry && (
          <div className={`rounded-xl border p-4 mb-6 ${isDarkMode ? "border-rose-800/50 bg-rose-950/20" : "border-rose-200 bg-rose-50"}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className={`text-sm font-semibold ${isDarkMode ? "text-rose-300" : "text-rose-700"}`}>Payment Failed</p>
                <p className={`mt-1 text-xs ${isDarkMode ? "text-rose-400/70" : "text-rose-600"}`}>{paymentRetry.error}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPaymentRetry(null);
                  handleProceedToPayment();
                }}
                disabled={submitting}
                className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition ${isDarkMode ? "bg-rose-600 text-white hover:bg-rose-700" : "bg-rose-600 text-white hover:bg-rose-700"} disabled:opacity-50`}
              >
                Retry Payment
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* ── 01 Administration ── */}
          <FormSection number="01" title="Administration" note="Auto-assigned on approval">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FieldBox label="Admission No.">
                <input
                  type="text"
                  value="Auto on submit"
                  readOnly
                  className={`${inputCls("x")} cursor-not-allowed opacity-50`}
                />
              </FieldBox>
              <FieldBox label="Admission Date">
                <input
                  type="text"
                  name="admissionDate"
                  value={formData.admissionDate}
                  readOnly
                  className={inputCls("admissionDate")}
                />
              </FieldBox>
            </div>
          </FormSection>

          {/* ── 02 Personal Information ── */}
          <FormSection number="02" title="Personal Information">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr_152px]">
              <FieldBox label="First Name" required error={err("firstName")}>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("firstName")} placeholder="First name" className={inputCls("firstName")} />
              </FieldBox>
              <FieldBox label="Last Name" required error={err("lastName")}>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("lastName")} placeholder="Last name" className={inputCls("lastName")} />
              </FieldBox>

              {/* Photo upload — spans rows */}
              <div className="lg:row-span-3">
                <p className={`mb-1.5 text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                  Student Photo <span className="text-rose-400">*</span>
                </p>
                <div
                  className={`relative flex min-h-[152px] cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border-2 border-dashed transition ${
                    uploads.studentPhoto
                      ? "border-teal-500/50 bg-teal-950/20"
                      : errors.studentPhoto
                        ? "border-rose-500/50 bg-rose-950/10"
                        : isDarkMode
                          ? "border-slate-700 bg-slate-800/40 hover:border-blue-500/50"
                          : "border-slate-300 bg-white hover:border-blue-400/50"
                  }`}
                >
                  {uploads.studentPhoto ? (
                    <img
                      src={URL.createObjectURL(uploads.studentPhoto)}
                      alt="student preview"
                      className="absolute inset-0 h-full w-full object-cover"
                      onClick={() => openPreview(uploads.studentPhoto)}
                    />
                  ) : (
                    <>
                      <FaCloudUploadAlt className="text-2xl text-slate-500" />
                      <p className="px-2 text-center text-[11px] text-slate-500">Upload photo</p>
                    </>
                  )}
                  <input type="file" name="studentPhoto" accept="image/*" onChange={handleFileChange} className="absolute inset-0 cursor-pointer opacity-0" />
                </div>
                {err("studentPhoto")}
              </div>

              <FieldBox label="Date of Birth" required error={err("dateOfBirth")}>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  max={new Date(Date.now() - 86400000).toISOString().split("T")[0]}
                  readOnly={isFieldReadOnlyForStudent("dateOfBirth")}
                  className={inputCls("dateOfBirth")}
                />
              </FieldBox>
              <FieldBox label="Gender" required error={err("gender")}>
                <select name="gender" value={formData.gender} onChange={handleChange} disabled={isFieldReadOnlyForStudent("gender")} className={inputCls("gender")}>
                  <option value="">Select gender</option>
                  {genderOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </FieldBox>
              <FieldBox label="Blood Group">
                <select name="bloodGroup" value={formData.bloodGroup} onChange={handleChange} disabled={isFieldReadOnlyForStudent("bloodGroup")} className={inputCls("bloodGroup")}>
                  <option value="">Select</option>
                  {bloodGroupOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </FieldBox>
              <FieldBox label="Class / Standard" required error={err("standard")}>
                <select name="standard" value={formData.standard} onChange={handleChange} disabled={isFieldReadOnlyForStudent("standard")} className={inputCls("standard")}>
                  <option value="">Select class</option>
                  {standardOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </FieldBox>
              <FieldBox label="Phone Number" required error={err("contactNumber")}>
                <input type="tel" name="contactNumber" value={formData.contactNumber} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("contactNumber")} placeholder="+91 XXXXX XXXXX" inputMode="numeric" className={inputCls("contactNumber")} />
              </FieldBox>
              <FieldBox label="Email" required error={err("email")}>
                <input type="email" name="email" value={formData.email} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("email")} placeholder="email@example.com" className={inputCls("email")} />
              </FieldBox>
              <FieldBox label="Aadhaar Number" required error={err("adharNumber")}>
                <input type="text" name="adharNumber" value={formData.adharNumber} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("adharNumber")} placeholder="12-digit number" inputMode="numeric" maxLength={12} className={inputCls("adharNumber")} />
              </FieldBox>
              <FieldBox label="WhatsApp / Alt. Number" error={err("emergencyContact")}>
                <input type="tel" name="emergencyContact" value={formData.emergencyContact} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("emergencyContact")} placeholder="Optional" inputMode="numeric" className={inputCls("emergencyContact")} />
              </FieldBox>
            </div>
          </FormSection>

          {/* ── 03 Guardian Information ── */}
          <FormSection number="03" title="Guardian Information">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr_152px]">
              <FieldBox label="Father Name">
                <input type="text" name="fatherName" value={formData.fatherName} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("fatherName")} placeholder="Father's full name" className={inputCls("fatherName")} />
              </FieldBox>
              <FieldBox label="Mother Name">
                <input type="text" name="motherName" value={formData.motherName} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("motherName")} placeholder="Mother's full name" className={inputCls("motherName")} />
              </FieldBox>
              <div className="lg:row-span-2">
                <p className={`mb-1.5 text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                  Aadhaar / CNIC Copy <span className="text-rose-400">*</span>
                </p>
                <DocZone field={uploadFields.aadharCard} onChange={handleFileChange} onPreview={openPreview} error={err("aadharCard")} />
              </div>
              <div className="md:col-span-1 lg:col-span-1">
                <FieldBox label="Guardian Occupation">
                  <input type="text" name="guardianOccupation" value={formData.guardianOccupation} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("guardianOccupation")} placeholder="Occupation" className={inputCls("guardianOccupation")} />
                </FieldBox>
              </div>
              <div className="md:col-span-1 lg:col-span-1">
                <FieldBox label="Pincode" error={err("pincode")}>
                  <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("pincode")} placeholder="6 digits" inputMode="numeric" maxLength={6} className={inputCls("pincode")} />
                </FieldBox>
              </div>
              <div className="md:col-span-2 lg:col-span-2">
                <FieldBox label="Present Address">
                  <textarea name="address" value={formData.address} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("address")} rows={3} placeholder="Full present address" className={inputCls("address")} />
                </FieldBox>
              </div>
              <FieldBox label="City">
                <input type="text" name="city" value={formData.city} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("city")} placeholder="City" className={inputCls("city")} />
              </FieldBox>
              <FieldBox label="State">
                <input type="text" name="state" value={formData.state} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("state")} placeholder="State" className={inputCls("state")} />
              </FieldBox>
            </div>
          </FormSection>

          {/* ── 04 Academic Background ── */}
          <FormSection number="04" title="Academic Background">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr_152px]">
              <FieldBox label="Last School Attended">
                <input type="text" name="previousSchool" value={formData.previousSchool} onChange={handleChange} readOnly={isFieldReadOnlyForStudent("previousSchool")} placeholder="Previous school name" className={inputCls("previousSchool")} />
              </FieldBox>
              <FieldBox label="Applying for Class" required error={err("standard")}>
                <select name="standard" value={formData.standard} onChange={handleChange} disabled={isFieldReadOnlyForStudent("standard")} className={inputCls("standard")}>
                  <option value="">Select class</option>
                  {standardOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </FieldBox>
              <div className="lg:row-span-2">
                <p className={`mb-1.5 text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>School Certificate</p>
                <DocZone field={uploadFields.previousMarksheet} onChange={handleFileChange} onPreview={openPreview} error={err("previousMarksheet")} />
              </div>
              <FieldBox label="Admission Date">
                <input type="text" name="admissionDate" value={formData.admissionDate} readOnly className={inputCls("admissionDate")} />
              </FieldBox>
              <div>
                <p className={`mb-1.5 text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Admission Fee</p>
                <div className={`flex h-[42px] items-center rounded-xl border px-4 text-sm font-bold ${isDarkMode ? "border-blue-500/25 bg-blue-950/25 text-blue-300" : "border-blue-400/40 bg-blue-50 text-blue-700"}`}>
                  {formatCurrency(formData.admissionAmount)}
                </div>
              </div>
            </div>
          </FormSection>

          {/* ── 05 Supporting Documents ── */}
          <FormSection number="05" title="Supporting Documents">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {Object.values(uploadFields)
                .filter((f) => f.name !== "studentPhoto" && f.name !== "aadharCard" && f.name !== "previousMarksheet")
                .map((field) => (
                  <div key={field.name}>
                    <p className={`mb-1.5 text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                      {field.label}
                      {field.required ? <span className="ml-1 text-rose-400">*</span> : null}
                    </p>
                    <DocZone field={field} onChange={handleFileChange} onPreview={openPreview} error={err(field.name)} compact />
                  </div>
                ))}
            </div>
            <div className="mt-5">
              <div className={`mb-1 flex items-center justify-between text-[11px] ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                <span>Upload quota</span>
                <span>{formatBytes(uploadedBytes)} / {formatBytes(TOTAL_UPLOAD_QUOTA_BYTES)}</span>
              </div>
              <div className={`overflow-hidden rounded-full ${isDarkMode ? "bg-slate-800" : "bg-slate-200"}`}>
                <div
                  className="h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 transition-all"
                  style={{ width: `${Math.min(100, (uploadedBytes / TOTAL_UPLOAD_QUOTA_BYTES) * 100)}%` }}
                />
              </div>
            </div>
          </FormSection>

          {/* ── Action bar ── */}
          <div className="flex items-center justify-between gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate("/user-dashboard")}
              className={`text-sm underline-offset-2 transition hover:underline ${isDarkMode ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700"}`}
            >
              ← Dashboard
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleReset}
                disabled={submitting}
                className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition disabled:opacity-50 ${isDarkMode ? "border-rose-800/50 bg-rose-950/40 text-rose-400 hover:bg-rose-950/70" : "border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"}`}
              >
                <FaTrashAlt className="text-xs" /> Reset
              </button>
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={submitting}
                className={`inline-flex items-center gap-2 rounded-xl border px-5 py-2.5 text-sm font-semibold transition disabled:opacity-50 ${isDarkMode ? "border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}
              >
                <FaRegSave /> Save Draft
              </button>
              {!savedAdmissionId ? (
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-teal-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/30 transition hover:opacity-90 disabled:opacity-60"
                >
                  {saving ? <><FaLock /> Saving…</> : <><FaRegSave /> Save Application</>}
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${isDarkMode ? "bg-teal-900/40 text-teal-300" : "bg-teal-100 text-teal-700"}`}>
                    ✓ Application Saved
                  </span>
                  <button
                    type="button"
                    onClick={handleProceedToPayment}
                    disabled={submitting}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-teal-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/30 transition hover:opacity-90 disabled:opacity-60"
                  >
                    {submitting ? <><FaLock /> Processing…</> : <><FaMoneyCheckAlt /> Proceed to Payment</>}
                  </button>
                </div>
              )}
            </div>
          </div>

        </form>
      </div>
    </div>
    </FormThemeCtx.Provider>
  );
}

const FormSection = ({ number, title, note, children }) => {
  const [open, setOpen] = React.useState(true);
  const dm = React.useContext(FormThemeCtx);
  return (
    <div className={`overflow-hidden border shadow-[0_8px_30px_-12px_rgba(0,0,0,0.25)] ${dm ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center gap-3 border-b px-5 py-4 text-left transition ${dm ? "border-slate-800 hover:bg-slate-800/50" : "border-slate-200 hover:bg-slate-50"}`}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-teal-600 text-[11px] font-black text-white shadow-sm shadow-blue-900/50">
          {number}
        </span>
        <div className="flex-1">
          <h3 className={`text-sm font-bold leading-none ${dm ? "text-white" : "text-slate-900"}`}>{title}</h3>
          {note ? <p className="mt-0.5 text-[11px] text-slate-500">{note}</p> : null}
        </div>
        <FaChevronDown
          className={`shrink-0 text-slate-400 transition-transform duration-300 ${
            open ? "rotate-180" : "rotate-0"
          }`}
        />
      </button>
      {open && <div className="p-5 sm:p-6">{children}</div>}
    </div>
  );
};

const FieldBox = ({ label, required, error, children }) => {
  const dm = React.useContext(FormThemeCtx);
  return (
    <div>
      <p className={`mb-1.5 text-[11px] font-semibold uppercase tracking-wider ${dm ? "text-slate-400" : "text-slate-600"}`}>
        {label}
        {required ? <span className="ml-1 text-rose-400">*</span> : null}
      </p>
      {children}
      {error}
    </div>
  );
};

const DocZone = ({ field, onChange, onPreview, error, compact }) => {
  const dm = React.useContext(FormThemeCtx);
  return (
  <div>
    <div
      className={`relative flex cursor-pointer flex-col items-center justify-center gap-1.5 overflow-hidden rounded-xl border-2 border-dashed transition ${
        compact ? "min-h-[100px]" : "min-h-[148px]"
      } ${
        field.file
          ? "border-teal-500/50 bg-teal-950/20"
          : dm
            ? "border-slate-700 bg-slate-800/40 hover:border-blue-500/50"
            : "border-slate-300 bg-slate-50 hover:border-blue-400/50"
      }`}
    >
      <FaCloudUploadAlt className={`text-xl ${field.file ? "text-teal-400" : "text-slate-500"}`} />
      <p className={`px-2 text-center text-[11px] leading-4 ${field.file ? "font-medium text-teal-300" : "text-slate-500"}`}>
        {field.file ? field.file.name : "Click to upload"}
      </p>
      {field.file ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPreview(field.file); }}
          className="mt-0.5 rounded-lg border border-teal-500/30 bg-teal-950/40 px-2.5 py-1 text-[10px] font-semibold text-teal-300 transition hover:bg-teal-950/70"
        >
          {isPdfFile(field.file) ? "Open PDF" : "Preview"}
        </button>
      ) : null}
      <input
        type="file"
        name={field.name}
        accept={field.accept}
        onChange={onChange}
        className="absolute inset-0 cursor-pointer opacity-0"
      />
    </div>
    {error}
  </div>
  );
};

